import Dictionary from '@/types/dictionary';

export enum DISCLAIMER_LEVEL {
  ERROR = 'DISCLAIMER_ERROR',
  WARNING = 'DISCLAIMER_WARNING',
  INFO = 'DISCLAIMER_INFO',
}

export enum DISCLAIMER_TAG {
  PROPOSAL = 'PROPOSAL',
  COMPARISON = 'COMPARISON',
}

export const DISCLAIMERS: Dictionary<{ i18nKey: string; level?: DISCLAIMER_LEVEL }> = {
  // Default disclaimer
  DEFAULT_MISSING_DATA: {
    i18nKey: 'DefaultMissingData',
  },

  // Generic disclaimers
  RISK_METRICS_NOT_ENOUGH_DATA: {
    i18nKey: 'RiskMetricsNotEnoughData',
    level: DISCLAIMER_LEVEL.WARNING,
  },

  NOT_APPLICABLE: {
    i18nKey: 'NotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },

  NOT_AGGREGATED: {
    i18nKey: 'NotAggregated',
    level: DISCLAIMER_LEVEL.INFO,
  },

  USING_ROLLING_BACKFILL: {
    i18nKey: 'UsingRollingBackfill',
    level: DISCLAIMER_LEVEL.INFO,
  },

  // Chart disclaimers
  GROWTH_CHART_NOT_ENOUGH_DATA: {
    i18nKey: 'GrowthChart.NotEnoughData',
    level: DISCLAIMER_LEVEL.WARNING,
  },

  CHART_ITEMS_MISSING_WEEKLY_DATA: {
    i18nKey: 'Charts.MissingWeeklyData',
    level: DISCLAIMER_LEVEL.WARNING,
  },

  HOLDING_MISSING_BENCHMARK: {
    i18nKey: 'HoldingMissingBenchmark',
    level: DISCLAIMER_LEVEL.WARNING,
  },

  // Edit portfolio disclaimers
  EDIT_PORTFOLIO_HOLDING_MISSING: {
    i18nKey: 'EditPortfolio.MissingProduct',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  EDIT_PORTFOLIO_HOLDING_NO_DATA: {
    i18nKey: 'EditPortfolio.NoData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  EDIT_PORTFOLIO_HOLDING_CLOSED: {
    i18nKey: 'EditPortfolio.ClosedHolding',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  EDIT_PORTFOLIO_HOLDING_LESS_THAN_1Y: {
    i18nKey: 'EditPortfolio.MissingData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  EDIT_PORTFOLIO_HOLDING_LESS_THAN_3Y: {
    i18nKey: 'EditPortfolio.MissingData',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  EDIT_PORTFOLIO_HOLDING_GIC: {
    i18nKey: 'EditPortfolio.GIC',
    level: DISCLAIMER_LEVEL.INFO,
  },
  EDIT_PORTFOLIO_HOLDING_HISA: {
    i18nKey: 'EditPortfolio.HISA',
    level: DISCLAIMER_LEVEL.INFO,
  },
  EDIT_PROTFOLIO_HOLDING_LATEST_WEEKLY_PRICE: {
    i18nKey: 'EditPortfolio.LatestWeeklyPrice',
    level: DISCLAIMER_LEVEL.WARNING,
  },

  // Instrument disclaimers
  INSTRUMENT_CUSTOM_DISCLAIMER: {
    i18nKey: '', // Not meant to be used directly, all we really need is the key
  },

  // Fund disclaimers
  FUND_LESS_THAN_1Y: {
    i18nKey: 'MissingFundData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  FUND_LESS_THAN_3Y: {
    i18nKey: 'MissingFundData',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_NO_DATA_BEFORE_DATE: {
    i18nKey: 'MissingFundData',
  },
  FUND_MISSING_BENCHMARK: {
    i18nKey: 'FundMissingBenchmark',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_NO_DATA: {
    i18nKey: 'FundNoData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  FUND_NO_NAV_DATA: {
    i18nKey: 'FundNoData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  FUND_CLOSED: {
    i18nKey: 'FundClosed',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  FUND_NAV_CLOSED: {
    i18nKey: 'FundClosed',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  FUND_NO_RISK_METRICS: {
    i18nKey: 'FundNoDataRiskMetrics',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_BENCHMARK_SHARPE: {
    i18nKey: 'FundNoSharpe',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_BENCHMARK_SORTINO: {
    i18nKey: 'FundNoSortino',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_BENCHMARK_UPDOWN: {
    i18nKey: 'FundNoUpDown',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_BENCHMARK_BETA: {
    i18nKey: 'FundNoBeta',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_BENCHMARK_RSQUARED: {
    i18nKey: 'FundNoRsquared',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_BENCHMARK_CORRELATION_COEFFICIENT: {
    i18nKey: 'FundNoCorrelationCoefficient',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_BENCHMARK_ALPHA: {
    i18nKey: 'FundNoAlpha',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_BENCHMARK_INFO_RATIO: {
    i18nKey: 'FundNoInfoRatio',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_BENCHMARK_TRACKING_ERROR: {
    i18nKey: 'FundNoTrackingError',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_TOP_HOLDINGS: {
    i18nKey: 'FundMissingTopHoldings',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_COUNTRY_ALLOC: {
    i18nKey: 'FundMissingCountryAlloc',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_BOND_INDUSTRY_HOLDINGS: {
    i18nKey: 'FundBondIndustryHoldings',
  },
  FUND_MISSING_INDUSTRY_HOLDINGS: {
    i18nKey: 'FundMissingIndustryHoldings',
  },
  FUND_MISSING_ASSET_DETAILS: {
    i18nKey: 'FundMissingAssetDetails',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_MATURITY: {
    i18nKey: 'FundMissingMaturity',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_CREDIT_QUALITY: {
    i18nKey: 'FundMissingCreditQuality',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_FIXED_INCOME_REGIONS: {
    i18nKey: 'FundMissingFixedIncomeRegions',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_FIXED_INCOME_STYLE: {
    i18nKey: 'FundMissingFixedIncomeStyle',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_MISSING_FIXED_INCOME_SECTORS: {
    i18nKey: 'FundMissingFixedIncomeSectors',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_HAS_SAME_BENCHMARK: {
    i18nKey: 'FundHasSameBenchmark',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_CREDIT_QUALITY_NOT_APPLICABLE: {
    i18nKey: 'FundCreditQualityNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  FUND_MATURITY_NOT_APPLICABLE: {
    i18nKey: 'FundMaturityNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  FUND_FIXED_INCOME_REGION_NOT_APPLICABLE: {
    i18nKey: 'FundFixedIncomeRegionNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  FUND_FIXED_INCOME_SECTORS_NOT_APPLICABLE: {
    i18nKey: 'FundFixedIncomeSectorNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  FUND_FIXED_INCOME_METRICS_NOT_APPLICABLE: {
    i18nKey: 'FundFixedIncomeMetricsNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  FUND_FIXED_INCOME_STYLE_NOT_APPLICABLE: {
    i18nKey: 'FundFixedIncomeStyleNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  FUND_MISSING_EQUITY_STYLE: {
    i18nKey: 'FundMissingEquityStyle',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  FUND_EQUITY_METRICS_NOT_APPLICABLE: {
    i18nKey: 'FundEquityMetricsNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  FUND_EQUITY_STYLE_NOT_APPLICABLE: {
    i18nKey: 'FundEquityStyleNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },

  // Stocks disclaimers
  STOCK_NO_DATA: {
    i18nKey: 'StockNoData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  STOCK_NO_NAV_DATA: {
    i18nKey: 'StockNoData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  STOCK_CLOSED: {
    i18nKey: 'StockClosed',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  STOCK_NAV_CLOSED: {
    i18nKey: 'StockClosed',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  STOCK_NO_DATA_BEFORE_DATE: {
    i18nKey: 'MissingStockData',
  },
  STOCK_LESS_THAN_1Y: {
    i18nKey: 'MissingStockData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  STOCK_LESS_THAN_3Y: {
    i18nKey: 'MissingStockData',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  STOCK_NO_RISK_METRICS: {
    i18nKey: 'StockNoDataRiskMetrics',
    level: DISCLAIMER_LEVEL.ERROR,
  },

  // Comparison portfolio disclaimers
  PORTFOLIO_MISSING_HOLDINGS: {
    i18nKey: 'MissingPortfolioHolding',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  PORTFOLIO_NO_DATA_BEFORE_DATE: {
    i18nKey: 'MissingPortfolioData',
  },
  PORTFOLIO_NO_DATA_BEFORE_DATE_SHORT: {
    i18nKey: 'MissingPortfolioDataShort',
  },
  PORTFOLIO_NO_DATA: {
    i18nKey: 'PortfolioNoData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  PORTFOLIO_CLOSED_HOLDINGS: {
    i18nKey: 'PortfolioHoldingClosed',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  PORTFOLIO_CLOSED_HOLDINGS_NAV: {
    i18nKey: 'PortfolioHoldingNavClosed',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  PORTFOLIO_NO_RISK_METRICS: {
    i18nKey: 'PortfolioNoDataRiskMetrics',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  PORTFOLIO_NO_BENCHMARK_SHARPE: {
    i18nKey: 'PortfolioNoSharpe',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_NO_BENCHMARK_SORTINO: {
    i18nKey: 'PortfolioNoSortino',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_NO_BENCHMARK_UPDOWN: {
    i18nKey: 'PortfolioNoUpDown',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_NO_BENCHMARK_BETA: {
    i18nKey: 'PortfolioNoBeta',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_NO_BENCHMARK_RSQUARED: {
    i18nKey: 'PortfolioNoRsquared',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_NO_BENCHMARK_ALPHA: {
    i18nKey: 'PortfolioNoAlpha',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_NO_BENCHMARK_TRACKING_ERROR: {
    i18nKey: 'PortfolioNoTrackingError',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_NO_BENCHMARK_INFO_RATIO: {
    i18nKey: 'PortfolioNoInfoRatio',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_NO_BENCHMARK_CORRELATION_COEFFICIENT: {
    i18nKey: 'PortfolioNoCorrelationCoefficient',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_LESS_THAN_1Y: {
    i18nKey: 'PortfolioLessThan1Y',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  PORTFOLIO_LESS_THAN_3Y: {
    i18nKey: 'PortfolioLessThan3Y',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_BENCHMARK: {
    i18nKey: 'PortfoliosMissingBenchmark',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_TOP_HOLDINGS: {
    i18nKey: 'PortfolioMissingTopHoldings',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_COUNTRY_ALLOC: {
    i18nKey: 'PortfolioMissingCountryAlloc',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_INDUSTRY_HOLDINGS: {
    i18nKey: 'PortfolioMissingIndustryHoldings',
  },
  PORTFOLIO_MISSING_ASSET_DETAILS: {
    i18nKey: 'PortfolioMissingAssetDetails',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_MATURITY: {
    i18nKey: 'PortfolioMissingMaturity',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_CREDIT_QUALITY: {
    i18nKey: 'PortfolioMissingCreditQuality',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_FIXED_INCOME_REGIONS: {
    i18nKey: 'PortfolioMissingFixedIncomeRegions',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_FIXED_INCOME_SECTORS: {
    i18nKey: 'PortfolioMissingFixedIncomeSectors',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_FIXED_INCOME_STYLE: {
    i18nKey: 'PortfolioMissingFixedIncomeStyle',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_MISSING_EQUITY_STYLE: {
    i18nKey: 'PortfolioMissingEquityStyle',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  PORTFOLIO_CREDIT_QUALITY_NOT_APPLICABLE: {
    i18nKey: 'PortfolioCreditQualityNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  PORTFOLIO_MATURITY_NOT_APPLICABLE: {
    i18nKey: 'PortfolioMaturityNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  PORTFOLIO_FIXED_INCOME_REGION_NOT_APPLICABLE: {
    i18nKey: 'PortfolioFixedIncomeRegionNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  PORTFOLIO_FIXED_INCOME_SECTORS_NOT_APPLICABLE: {
    i18nKey: 'PortfolioFixedIncomeSectorNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  PORTFOLIO_FIXED_INCOME_METRICS_NOT_APPLICABLE: {
    i18nKey: 'PortfolioFixedIncomeMetricsNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  PORTFOLIO_FIXED_INCOME_STYLE_NOT_APPLICABLE: {
    i18nKey: 'PortfolioFixedIncomeStyleNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  PORTFOLIO_EQUITY_METRICS_NOT_APPLICABLE: {
    i18nKey: 'PortfolioEquityMetricsNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  PORTFOLIO_EQUITY_STYLE_NOT_APPLICABLE: {
    i18nKey: 'PortfolioEquityStyleNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  SHORT_HOLDINGS: {
    i18nKey: '', // Not meant to be used directly, all we really need is the key
  },

  // Group disclaimers
  GROUP_NO_DATA_BEFORE_DATE: {
    i18nKey: 'MissingGroupData',
  },
  GROUP_NO_DATA_BEFORE_DATE_SHORT: {
    i18nKey: 'MissingGroupDataShort',
  },
  GROUP_NO_DATA: {
    i18nKey: 'GroupNoData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  GROUP_CLOSED_HOLDINGS: {
    i18nKey: 'GroupHoldingClosed',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  GROUP_CLOSED_HOLDINGS_NAV: {
    i18nKey: 'GroupHoldingNavClosed',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  GROUP_NO_RISK_METRICS: {
    i18nKey: 'GroupNoDataRiskMetrics',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  GROUP_NO_BENCHMARK_SHARPE: {
    i18nKey: 'GroupNoSharpe',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_NO_BENCHMARK_SORTINO: {
    i18nKey: 'GroupNoSortino',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_NO_BENCHMARK_UPDOWN: {
    i18nKey: 'GroupNoUpDown',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_NO_BENCHMARK_BETA: {
    i18nKey: 'GroupNoBeta',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_NO_BENCHMARK_RSQUARED: {
    i18nKey: 'GroupNoRsquared',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_NO_BENCHMARK_ALPHA: {
    i18nKey: 'GroupNoAlpha',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_NO_BENCHMARK_TRACKING_ERROR: {
    i18nKey: 'GroupNoTrackingError',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_NO_BENCHMARK_INFO_RATIO: {
    i18nKey: 'GroupNoInfoRatio',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_NO_BENCHMARK_CORRELATION_COEFFICIENT: {
    i18nKey: 'GroupNoCorrelationCoefficient',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_LESS_THAN_1Y: {
    i18nKey: 'GroupLessThan1Y',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  GROUP_LESS_THAN_3Y: {
    i18nKey: 'GroupLessThan3Y',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_BENCHMARK: {
    i18nKey: 'GroupsMissingBenchmark',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_TOP_HOLDINGS: {
    i18nKey: 'GroupMissingTopHoldings',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_COUNTRY_ALLOC: {
    i18nKey: 'GroupMissingCountryAlloc',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_INDUSTRY_HOLDINGS: {
    i18nKey: 'GroupMissingIndustryHoldings',
  },
  GROUP_MISSING_ASSET_DETAILS: {
    i18nKey: 'GroupMissingAssetDetails',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_MATURITY: {
    i18nKey: 'GroupMissingMaturity',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_CREDIT_QUALITY: {
    i18nKey: 'GroupMissingCreditQuality',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_FIXED_INCOME_REGIONS: {
    i18nKey: 'GroupMissingFixedIncomeRegions',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_FIXED_INCOME_SECTORS: {
    i18nKey: 'GroupMissingFixedIncomeSectors',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_FIXED_INCOME_STYLE: {
    i18nKey: 'GroupMissingFixedIncomeStyle',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_MISSING_EQUITY_STYLE: {
    i18nKey: 'GroupMissingEquityStyle',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  GROUP_CREDIT_QUALITY_NOT_APPLICABLE: {
    i18nKey: 'GroupCreditQualityNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  GROUP_MATURITY_NOT_APPLICABLE: {
    i18nKey: 'GroupMaturityNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  GROUP_FIXED_INCOME_REGION_NOT_APPLICABLE: {
    i18nKey: 'GroupFixedIncomeRegionNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  GROUP_FIXED_INCOME_SECTORS_NOT_APPLICABLE: {
    i18nKey: 'GroupFixedIncomeSectorNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  GROUP_FIXED_INCOME_METRICS_NOT_APPLICABLE: {
    i18nKey: 'GroupFixedIncomeMetricsNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  GROUP_FIXED_INCOME_STYLE_NOT_APPLICABLE: {
    i18nKey: 'GroupFixedIncomeStyleNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  GROUP_EQUITY_METRICS_NOT_APPLICABLE: {
    i18nKey: 'GroupEquityMetricsNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },
  GROUP_EQUITY_STYLE_NOT_APPLICABLE: {
    i18nKey: 'GroupEquityStyleNotApplicable',
    level: DISCLAIMER_LEVEL.INFO,
  },

  // Benchmark Disclaimers
  BENCHMARK_LESS_THAN_1Y: {
    i18nKey: 'MissingBenchmarkData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  BENCHMARK_LESS_THAN_3Y: {
    i18nKey: 'MissingBenchmarkData',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  BENCHMARK_NO_DATA_BEFORE_DATE: {
    i18nKey: 'MissingBenchmarkData',
  },
  BENCHMARK_NO_DATA: {
    i18nKey: 'BenchmarkNoData',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  BENCHMARK_CLOSED: {
    i18nKey: 'BenchmarkClosed',
    level: DISCLAIMER_LEVEL.ERROR,
  },
  BENCHMARK_NO_RISK_METRICS: {
    i18nKey: 'BenchmarkNoDataRiskMetrics',
    level: DISCLAIMER_LEVEL.WARNING,
  },
  BENCHMARK_PROVIDERS: {
    i18nKey: '', // Not meant to be used directly, all we really need is the key
  },

  // Custom provider fields
  CUSTOM_PROVIDER_RISK_RATING: {
    i18nKey: 'CustomProviderRiskRating',
  },
};

export const DISCLAIMER_KEYS = Object.keys(DISCLAIMERS).reduce((acc, key) => {
  acc[key] = key;
  return acc;
}, {} as Dictionary<string>);

export const DISCLAIMER_FOLLOWING_RELEVANCE_PERIOD = ['OVERVIEW', 'TRAILING_RETURNS'];

export default {
  DISCLAIMERS,
  DISCLAIMER_KEYS,
  DISCLAIMER_LEVEL,
};
